import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Col,
  Collapse,
  Dropdown,
  InputNumber,
  Menu,
  Popconfirm,
  Row,
  Select,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from 'util/api';
import {
  MARGIN_TARGET_FIELD_LABELS,
  MARGIN_TARGET_GUIDE_RANGES,
  MARGIN_TARGET_PRICE_FIELDS,
  MARGIN_TARGET_DEFAULT_RANGE,
  GST_MULT,
  resolveTargetPct,
  buildTargetMap,
} from '../marginUtils';
import {
  IMPLIED_KEG_TARGET_PCT,
  KEG_FALLBACK_DIVISORS,
  KEG_POUR_VOLUME_L,
  PINT_MAX_ABV,
  targetPctToDivisor,
} from '../kegPricingSuggestions';
import MondayFormulaCheck from './MondayFormulaCheck';
import PricingExample from './PricingExample';
import './PricingGuide.less';

const { Text, Title } = Typography;

const OVERRIDE_RANGES = MARGIN_TARGET_GUIDE_RANGES.filter(
  (r) => r.value !== MARGIN_TARGET_DEFAULT_RANGE,
);

const POUR_FORMULA_ROWS = [
  {
    key: 'priceVenuePerLitre',
    snake: 'price_venue_per_litre',
    label: 'Venue/L (tap fill)',
    formula: 'Keg $/L ÷ margin factor × 1.15, round down to 50¢. Extra margin if strong or Wild',
  },
  {
    key: 'pricePint',
    snake: 'price_pint',
    label: 'Pint (440ml)',
    formula: `(Keg $/L × 0.44) ÷ margin factor × 1.15, nearest 50¢. Skip over ${PINT_MAX_ABV}% ABV`,
  },
  {
    key: 'priceHalfPint',
    snake: 'price_half_pint',
    label: 'Half Pint (285ml)',
    formula: '(Keg $/L × 0.285) ÷ margin factor × 1.15, nearest 50¢. Extra margin if strong or Wild',
  },
  {
    key: 'price150ml',
    snake: 'price_150ml',
    label: '150ml Pour',
    formula: '(Keg $/L × 0.150) ÷ margin factor × 1.15, nearest 50¢. Extra margin if strong or Wild',
  },
];

const HIGH_ABV_FIELDS = [
  'price_venue_per_litre',
  'price_pint',
  'price_half_pint',
  'price_150ml',
];

const ROUNDING_META = {
  ceil_50: { label: '↑ 50¢', tip: 'Round up to 50¢' },
  nearest_50: { label: '~ 50¢', tip: 'Nearest 50¢' },
  floor_50: { label: '↓ 50¢', tip: 'Round down to 50¢' },
  nearest_cent: { label: '¢', tip: 'Nearest cent' },
};

const DEFAULT_ROUNDING = {
  rrp_unit: 'ceil_50',
  rrp_pack: 'ceil_50',
  price_venue_per_litre: 'floor_50',
  price_pint: 'nearest_50',
  price_half_pint: 'nearest_50',
  price_150ml: 'nearest_50',
};

function rowKey(range, priceField, condition = '') {
  return `${range}:${priceField}:${condition}`;
}

function rangeHasTargets(byKey, rangeValue, fields, condition) {
  return fields.some((field) => byKey[rowKey(rangeValue, field, condition)]);
}

function formatNiceNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  const rounded = Math.round(Number(n) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function RoundingSelect({ value, onChange }) {
  const meta = ROUNDING_META[value] || ROUNDING_META.nearest_50;
  return (
    <Tooltip title={meta.tip}>
      <div className="pricing-guide-rounding-wrap">
        <Select
          size="middle"
          className="pricing-guide-rounding"
          value={value}
          optionLabelProp="label"
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 168 }}
          onChange={onChange}
        >
          {Object.entries(ROUNDING_META).map(([optValue, optMeta]) => (
            <Select.Option key={optValue} value={optValue} label={optMeta.label}>
              {optMeta.tip}
            </Select.Option>
          ))}
        </Select>
      </div>
    </Tooltip>
  );
}

RoundingSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function formatPctWithDivisor(pct, divisor) {
  if (pct == null || Number.isNaN(pct)) return '—';
  const d = divisor != null ? Number(divisor).toFixed(2) : null;
  return (
    <Tooltip title={d != null ? `Margin factor ${d}` : undefined}>
      <span>
        {formatNiceNumber(pct)}
        %
      </span>
    </Tooltip>
  );
}

function existingTargetPct(existing) {
  if (!existing) return null;
  const n = parseFloat(existing.targetGpPct ?? existing.target_gp_pct);
  return Number.isNaN(n) ? null : n;
}

function TargetCell({
  editing, value, impliedHint, onChange, disabled,
}) {
  if (!editing) {
    if (value != null && !Number.isNaN(value)) {
      return (
        <Text strong className="pricing-guide-target-value">
          {formatNiceNumber(value)}
          %
        </Text>
      );
    }
    if (impliedHint != null) {
      return (
        <div className="pricing-guide-cell">
          <Text type="secondary" className="pricing-guide-target-value">
            {formatNiceNumber(impliedHint)}
            %
          </Text>
          <Text className="pricing-guide-hint">default</Text>
        </div>
      );
    }
    return (
      <Text type="secondary" className="pricing-guide-target-value">—</Text>
    );
  }

  return (
    <div className="pricing-guide-cell">
      <InputNumber
        min={0}
        max={99.9}
        step={0.5}
        size="middle"
        disabled={disabled}
        value={value}
        placeholder={impliedHint != null ? `${formatNiceNumber(impliedHint)}%` : '—'}
        formatter={(v) => {
          if (v === '' || v == null) return '';
          const nice = formatNiceNumber(v);
          return nice === '' ? '' : `${nice}%`;
        }}
        parser={(v) => String(v).replace(/%/g, '')}
        onChange={onChange}
      />
      {impliedHint != null && value == null && (
        <Text className="pricing-guide-hint">
          default
          {' '}
          {formatNiceNumber(impliedHint)}
          %
        </Text>
      )}
    </div>
  );
}

TargetCell.propTypes = {
  editing: PropTypes.bool.isRequired,
  value: PropTypes.number,
  impliedHint: PropTypes.number,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

TargetCell.defaultProps = {
  value: null,
  impliedHint: undefined,
  onChange: undefined,
  disabled: false,
};

function LockedRounding({ value }) {
  const meta = ROUNDING_META[value] || ROUNDING_META.nearest_50;
  return (
    <Tooltip title={meta.tip}>
      <Text type="secondary" className="pricing-guide-rounding-locked">
        {meta.label}
      </Text>
    </Tooltip>
  );
}

LockedRounding.propTypes = {
  value: PropTypes.string.isRequired,
};

function MarginsCardActions({
  editing, saving, dirty, onEdit, onCancel, onSave, addOverride,
}) {
  if (!editing) {
    return (
      <Button size="small" icon={<EditOutlined />} onClick={onEdit}>
        Edit
      </Button>
    );
  }
  return (
    <span className="pricing-guide-rules-actions">
      {addOverride}
      <Button size="small" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
      <Popconfirm
        title="Save margin target changes?"
        okText="Save"
        cancelText="Back"
        onConfirm={onSave}
        disabled={!dirty || saving}
      >
        <Button
          size="small"
          type="primary"
          loading={saving}
          disabled={!dirty}
        >
          Save
        </Button>
      </Popconfirm>
    </span>
  );
}

MarginsCardActions.propTypes = {
  editing: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  addOverride: PropTypes.node,
};

MarginsCardActions.defaultProps = {
  addOverride: null,
};

const DEFAULT_RULE_VALUES = {
  pour_uplift_abv: '10',
  pour_uplift_pct: '2',
  progressive_unit_markup: '0.0265',
  on_prem_case_uplift_330: '6',
  on_prem_case_uplift_440_bottle: '8',
  op_divisor_330: '0.32',
  op_divisor_440: '0.45',
  op_divisor_specialty: '0.50',
  slab_case_discount_large: '0.10',
  slab_case_discount_small: '0.075',
  slab_case_large_units: '24',
  progressive_off_prem_skus: '',
};

const RULE_DEFS = [
  {
    key: 'pour_uplift_abv',
    label: 'Strong beer ABV from',
    tip: 'At or above this ABV, Venue/L / Half Pint / 150ml use Strong / Wild margins',
    step: 0.5,
    ui: 'abv',
  },
  {
    key: 'pour_uplift_pct',
    label: 'Extra pour margin (strong / Wild)',
    tip: 'Extra GP percentage points on Venue/L, Half Pint, and 150ml when strong or Wild',
    step: 0.5,
    ui: 'gp_extra',
  },
  {
    key: 'on_prem_case_uplift_330',
    label: 'Off→On case uplift · 330ml',
    tip: 'Dollars added to Off Prem case price for 330ml cans. Kegs share On/Off.',
    step: 1,
    ui: 'money',
  },
  {
    key: 'on_prem_case_uplift_440_bottle',
    label: 'Off→On case uplift · 440 / bottle',
    tip: 'Dollars added to Off Prem case for 440ml cans and bottles. Kegs share On/Off.',
    step: 1,
    ui: 'money',
  },
  {
    key: 'progressive_unit_markup',
    label: 'Progressive retail markup',
    tip: 'Added on top of RRP Unit for Progressive retail unit (nearest 50¢)',
    step: 0.05,
    ui: 'markup_pct',
  },
  {
    key: 'op_divisor_330',
    label: 'Can-pour margin · 330ml',
    tip: 'Target GP on Off Prem unit cost. O.P. = cost ÷ (1 − GP%) × 1.15',
    step: 0.5,
    ui: 'margin_pct',
  },
  {
    key: 'op_divisor_440',
    label: 'Can-pour margin · 440ml',
    tip: 'Target GP on Off Prem unit cost. O.P. = cost ÷ (1 − GP%) × 1.15',
    step: 0.5,
    ui: 'margin_pct',
  },
  {
    key: 'op_divisor_specialty',
    label: 'Can-pour margin · Wild',
    tip: 'Target GP for Wild / specialty can pours. O.P. = cost ÷ (1 − GP%) × 1.15',
    step: 0.5,
    ui: 'margin_pct',
  },
  {
    key: 'slab_case_discount_large',
    label: 'Slab discount · large',
    tip: 'Off RRP Unit × case units for slabs at or above the large-unit threshold (Shopify + playground RRP Case)',
    step: 0.5,
    ui: 'discount_pct',
  },
  {
    key: 'slab_case_discount_small',
    label: 'Slab discount · small',
    tip: 'Off RRP Unit × case units for smaller slabs (15×440, etc.)',
    step: 0.5,
    ui: 'discount_pct',
  },
  {
    key: 'slab_case_large_units',
    label: 'Large slab from',
    tip: 'Case unit count at or above this uses the large slab discount (default 24)',
    step: 1,
    ui: 'count',
  },
];

function mergeRules(incoming) {
  return { ...DEFAULT_RULE_VALUES, ...(incoming || {}) };
}

/** Stored progressive (0.0265 / 2.65 / 1.0265) → UI percent points (2.65). */
function progressiveToUiPct(raw) {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) return 2.65;
  if (n > 1 && n < 1.5) return Math.round((n - 1) * 10000) / 100;
  if (n >= 1.5) return n;
  return Math.round(n * 10000) / 100;
}

/** Stored can-pour divisor (0.32) → UI margin % (68). */
function divisorToUiMargin(raw) {
  const d = parseFloat(raw);
  if (Number.isNaN(d) || d <= 0 || d >= 1) return null;
  return Math.round((1 - d) * 1000) / 10;
}

/** UI margin % (68) → stored divisor (0.32). */
function uiMarginToDivisor(pct) {
  const p = parseFloat(pct);
  if (Number.isNaN(p) || p <= 0 || p >= 100) return null;
  return String(Math.round((1 - p / 100) * 10000) / 10000);
}

function storageToUi(def, raw) {
  const fallback = DEFAULT_RULE_VALUES[def.key];
  if (def.ui === 'markup_pct') return progressiveToUiPct(raw ?? fallback);
  if (def.ui === 'margin_pct') {
    const m = divisorToUiMargin(raw ?? fallback);
    return m != null ? m : divisorToUiMargin(fallback);
  }
  if (def.ui === 'discount_pct') {
    const n = parseFloat(raw ?? fallback);
    const frac = Number.isNaN(n) ? parseFloat(fallback) : n;
    return Math.round(frac * 1000) / 10;
  }
  const n = parseFloat(raw ?? fallback);
  return Number.isNaN(n) ? parseFloat(fallback) : n;
}

function uiToStorage(def, uiValue) {
  if (def.ui === 'markup_pct') return String(uiValue);
  if (def.ui === 'margin_pct') return uiMarginToDivisor(uiValue);
  if (def.ui === 'discount_pct') {
    const p = parseFloat(uiValue);
    if (Number.isNaN(p) || p < 0 || p >= 100) return null;
    return String(Math.round((p / 100) * 1000) / 1000);
  }
  return String(uiValue);
}

function rulesToDraft(rules) {
  const merged = mergeRules(rules);
  const draft = {};
  RULE_DEFS.forEach((def) => {
    draft[def.key] = storageToUi(def, merged[def.key]);
  });
  return draft;
}

function ruleInputMin(def) {
  if (def.ui === 'money' || def.ui === 'discount_pct') return 0;
  if (def.ui === 'count') return 1;
  return 0.01;
}

function ruleInputMax(def) {
  if (def.ui === 'money' || def.ui === 'count') return 999;
  return 99.9;
}

function formatRuleDisplay(def, raw) {
  const ui = storageToUi(def, raw);
  if (ui == null || Number.isNaN(ui)) return '—';
  if (def.ui === 'abv') return `${formatNiceNumber(ui)}% ABV`;
  if (def.ui === 'gp_extra') return `+${formatNiceNumber(ui)}% GP`;
  if (def.ui === 'money') return `+$${formatNiceNumber(ui)} / case`;
  // Keep 2dp — formatNiceNumber rounds to 1dp (2.65 → 2.7).
  if (def.ui === 'markup_pct') return `+${Number(ui).toFixed(2)}%`;
  if (def.ui === 'margin_pct') return `${formatNiceNumber(ui)}% GP`;
  if (def.ui === 'discount_pct') return `−${formatNiceNumber(ui)}% / slab`;
  if (def.ui === 'count') return `${formatNiceNumber(ui)} units`;
  return String(ui);
}

function progressiveChannelLabel(raw) {
  const n = parseFloat(raw);
  let mult = 1.0265;
  if (!Number.isNaN(n) && n > 0) {
    if (n > 1 && n < 1.5) mult = n;
    else if (n >= 1.5) mult = 1 + n / 100;
    else mult = 1 + n;
  }
  const pct = ((mult - 1) * 100).toFixed(2);
  const multStr = String(Math.round(mult * 10000) / 10000);
  return { mult: multStr, pct };
}

const OFF_PREM_SKU_RULE = 'progressive_off_prem_skus';

function parseOffPremSkuKeys(raw) {
  return String(raw ?? '').split(/[^0-9]+/).filter(Boolean);
}

function serializeOffPremSkuKeys(keys) {
  const seen = new Set();
  const unique = [];
  (keys || []).forEach((key) => {
    const s = String(key).trim();
    if (!/^\d+$/.test(s) || seen.has(s)) return;
    seen.add(s);
    unique.push(s);
  });
  return unique.join(',');
}

function offPremSkuLabel(key, skuByKey) {
  const name = skuByKey[key]?.name;
  return name ? String(name) : `SKU ${key}`;
}

function RulesCard({ rules, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => rulesToDraft(rules));
  const [skuKeys, setSkuKeys] = useState(
    () => parseOffPremSkuKeys(rules[OFF_PREM_SKU_RULE]),
  );
  const [skuRows, setSkuRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.getAllProductSkus()
      .then((rows) => {
        if (!cancelled) setSkuRows(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSkuRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editing) {
      setDraft(rulesToDraft(rules));
      setSkuKeys(parseOffPremSkuKeys(rules[OFF_PREM_SKU_RULE]));
    }
  }, [rules, editing]);

  const skuByKey = useMemo(() => {
    const map = {};
    skuRows.forEach((sku) => {
      const key = sku.key ?? sku.id;
      if (key == null || key === '') return;
      map[String(key)] = sku;
    });
    return map;
  }, [skuRows]);

  const skuOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    skuRows.forEach((sku) => {
      const ct = String(sku.container_type ?? sku.containerType ?? '').toLowerCase();
      if (ct === 'keg') return;
      const key = String(sku.key ?? sku.id ?? '');
      if (!/^\d+$/.test(key) || seen.has(key)) return;
      seen.add(key);
      opts.push({
        value: key,
        label: sku.name ? String(sku.name) : `SKU ${key}`,
      });
    });
    skuKeys.forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      opts.push({ value: key, label: `SKU ${key}` });
    });
    opts.sort((a, b) => a.label.localeCompare(
      b.label,
      undefined,
      { numeric: true },
    ));
    return opts;
  }, [skuRows, skuKeys]);

  const savedDraft = rulesToDraft(rules);
  const valid = RULE_DEFS.every((def) => {
    const n = draft[def.key];
    return n != null && !Number.isNaN(n);
  });
  const savedSkuList = serializeOffPremSkuKeys(
    parseOffPremSkuKeys(rules[OFF_PREM_SKU_RULE]),
  );
  const dirty = RULE_DEFS.some((def) => draft[def.key] !== savedDraft[def.key])
    || serializeOffPremSkuKeys(skuKeys) !== savedSkuList;
  const skuListLabel = skuKeys.length
    ? skuKeys.map((key) => offPremSkuLabel(key, skuByKey)).join(', ')
    : 'None — only 4×6';

  const startEdit = () => {
    setDraft(rulesToDraft(rules));
    setSkuKeys(parseOffPremSkuKeys(rules[OFF_PREM_SKU_RULE]));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(rulesToDraft(rules));
    setSkuKeys(parseOffPremSkuKeys(rules[OFF_PREM_SKU_RULE]));
    setEditing(false);
  };

  const confirmSave = async () => {
    if (!valid || !dirty) {
      setEditing(false);
      return;
    }
    const patch = {};
    RULE_DEFS.forEach((def) => {
      const stored = uiToStorage(def, draft[def.key]);
      if (stored == null) return;
      patch[def.key] = stored;
    });
    if (Object.keys(patch).length !== RULE_DEFS.length) {
      message.error('Every rule needs a valid number');
      return;
    }
    const storedSkus = serializeOffPremSkuKeys(skuKeys);
    if (storedSkus.length > 255) {
      message.error('SKU list is too long. Remove some SKUs.');
      return;
    }
    patch[OFF_PREM_SKU_RULE] = storedSkus;
    setSaving(true);
    try {
      await onSave(patch);
      message.success('Rules saved');
      setEditing(false);
    } catch (err) {
      message.error(err?.message || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const extra = editing ? (
    <span className="pricing-guide-rules-actions">
      <Button size="small" onClick={cancelEdit} disabled={saving}>
        Cancel
      </Button>
      <Popconfirm
        title="Save these rules? Applies to suggestions, Example beer, and Monday check."
        okText="Save"
        cancelText="Back"
        onConfirm={confirmSave}
        disabled={!valid || !dirty || saving}
      >
        <Button
          size="small"
          type="primary"
          loading={saving}
          disabled={!valid || !dirty}
        >
          Save
        </Button>
      </Popconfirm>
    </span>
  ) : (
    <Button size="small" icon={<EditOutlined />} onClick={startEdit}>
      Edit
    </Button>
  );

  return (
    <Card
      size="small"
      title="Rules"
      extra={extra}
      className="pricing-guide-section"
    >
      <Row gutter={[12, 16]} className="pricing-guide-rules-grid">
        {RULE_DEFS.map((def) => {
          const labelNode = def.tip ? (
            <Tooltip title={def.tip}>
              <Text type="secondary" className="pricing-guide-rule-label">
                {def.label}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" className="pricing-guide-rule-label">
              {def.label}
            </Text>
          );
          return (
            <Col xs={12} md={6} key={def.key}>
              <div className="pricing-guide-rule">
                {labelNode}
                {editing ? (
                  <InputNumber
                    size="middle"
                    step={def.step}
                    min={ruleInputMin(def)}
                    max={ruleInputMax(def)}
                    precision={def.ui === 'count' ? 0 : undefined}
                    value={draft[def.key]}
                    onChange={(v) => setDraft((prev) => ({
                      ...prev,
                      [def.key]: v,
                    }))}
                    formatter={(v) => {
                      if (v === '' || v == null) return '';
                      if (def.ui === 'money') return `$${v}`;
                      if (def.ui === 'markup_pct' || def.ui === 'gp_extra') {
                        return `+${v}%`;
                      }
                      if (def.ui === 'discount_pct') return `−${v}%`;
                      if (
                        def.ui === 'abv'
                        || def.ui === 'margin_pct'
                      ) {
                        return `${v}%`;
                      }
                      return String(v);
                    }}
                    parser={(v) => String(v)
                      .replace(/%/g, '')
                      .replace(/\$/g, '')
                      .replace(/[+−-]/g, '')}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Text strong className="pricing-guide-rule-value">
                    {formatRuleDisplay(def, rules[def.key])}
                  </Text>
                )}
              </div>
            </Col>
          );
        })}
        <Col xs={24}>
          <div className="pricing-guide-rule">
            <Tooltip title="These SKUs use Off Prem with no +2.65%, same as 4×6. Kegs ignored. 4×6 always stays Off Prem.">
              <Text type="secondary" className="pricing-guide-rule-label">
                Progressive stays Off Prem
              </Text>
            </Tooltip>
            {editing ? (
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Search SKUs"
                value={skuKeys}
                options={skuOptions}
                onChange={(vals) => setSkuKeys(vals || [])}
                filterOption={(input, option) => (
                  String(option?.label || '')
                    .toLowerCase()
                    .includes(String(input).toLowerCase())
                )}
                style={{ width: '100%' }}
                disabled={saving}
                maxTagCount="responsive"
              />
            ) : (
              <Text strong className="pricing-guide-rule-value">
                {skuListLabel}
              </Text>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
}

RulesCard.propTypes = {
  rules: PropTypes.objectOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
};

function AddRangeOverride({ available, onAdd }) {
  if (!available.length) return null;
  return (
    <Dropdown
      trigger={['click']}
      overlay={(
        <Menu onClick={({ key }) => onAdd(key)}>
          {available.map((range) => (
            <Menu.Item key={range.value}>{range.label}</Menu.Item>
          ))}
        </Menu>
      )}
    >
      <Button type="link" size="small" icon={<PlusOutlined />}>
        Add range override
      </Button>
    </Dropdown>
  );
}

AddRangeOverride.propTypes = {
  available: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  onAdd: PropTypes.func.isRequired,
};

const PricingGuide = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState(() => ({ ...DEFAULT_RULE_VALUES }));
  /** Local pins so empty override rows stay visible until filled or dismissed. */
  const [pinnedRanges, setPinnedRanges] = useState({
    '': [],
    high_abv_or_wild: [],
  });
  /** Saved override rows hidden in this edit; Save deletes their targets. */
  const [removedRanges, setRemovedRanges] = useState({
    '': [],
    high_abv_or_wild: [],
  });
  const [marginEdit, setMarginEdit] = useState({
    '': false,
    high_abv_or_wild: false,
  });
  /** Dirty margin cells while a matrix is unlocked. Key = rowKey(...). */
  const [marginDraft, setMarginDraft] = useState({});
  const [savingMargins, setSavingMargins] = useState(false);

  const pinRange = (condition, rangeValue) => {
    const key = condition || '';
    setRemovedRanges((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((r) => r !== rangeValue),
    }));
    setMarginDraft((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const d = next[k];
        if (d.condition === condition && d.range === rangeValue && d.pct == null) {
          delete next[k];
        }
      });
      return next;
    });
    setPinnedRanges((prev) => {
      const list = prev[key] || [];
      if (list.includes(rangeValue)) return prev;
      return { ...prev, [key]: [...list, rangeValue] };
    });
  };

  const removeRangeOverride = useCallback((condition, rangeValue, record) => {
    if (rangeValue === MARGIN_TARGET_DEFAULT_RANGE) return;
    const key = condition || '';
    setPinnedRanges((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((r) => r !== rangeValue),
    }));

    const fields = condition === 'high_abv_or_wild'
      ? HIGH_ABV_FIELDS
      : MARGIN_TARGET_PRICE_FIELDS;
    setMarginDraft((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        const k = rowKey(rangeValue, field, condition);
        const existing = record?.[field];
        const id = existing?.id ?? next[k]?.id;
        if (id) {
          next[k] = {
            pct: null,
            rounding: existing?.rounding
              || next[k]?.rounding
              || DEFAULT_ROUNDING[field]
              || 'nearest_50',
            id,
            range: rangeValue,
            priceField: field,
            condition,
          };
        } else {
          delete next[k];
        }
      });
      return next;
    });

    if (record?.hasSavedTargets) {
      setRemovedRanges((prev) => {
        const list = prev[key] || [];
        if (list.includes(rangeValue)) return prev;
        return { ...prev, [key]: [...list, rangeValue] };
      });
    }
  }, []);

  const loadAll = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    try {
      const [targets, ruleMap] = await Promise.all([
        api.listMarginTargets(),
        api.listPricingRules(),
      ]);
      setRows(targets || []);
      setRules(mergeRules(ruleMap));
    } catch {
      message.error('Failed to load margin targets');
      setRows([]);
      setRules({ ...DEFAULT_RULE_VALUES });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const saveRules = async (patch) => {
    const next = await api.upsertPricingRules(patch);
    setRules(mergeRules(next));
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const byKey = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => {
      const range = r.productRange ?? r.product_range ?? r.range;
      const field = r.priceField ?? r.price_field;
      const condition = String(r.condition ?? '');
      if (range != null && field != null) {
        map[rowKey(String(range).trim(), field, condition)] = r;
      }
    });
    return map;
  }, [rows]);

  const targetMap = useMemo(() => buildTargetMap(rows), [rows]);

  const buildMatrix = useCallback((condition) => {
    const fields = condition === 'high_abv_or_wild'
      ? HIGH_ABV_FIELDS
      : MARGIN_TARGET_PRICE_FIELDS;
    const pinKey = condition || '';
    const pinned = pinnedRanges[pinKey] || [];
    const removed = removedRanges[pinKey] || [];

    return MARGIN_TARGET_GUIDE_RANGES
      .filter((range) => {
        if (range.value === MARGIN_TARGET_DEFAULT_RANGE) return true;
        if (removed.includes(range.value)) return false;
        if (pinned.includes(range.value)) return true;
        return rangeHasTargets(byKey, range.value, fields, condition);
      })
      .map((range) => {
        const record = {
          key: `${range.value}:${condition || 'base'}`,
          rangeLabel: range.label,
          range: range.value,
          hasSavedTargets: rangeHasTargets(byKey, range.value, fields, condition),
        };
        fields.forEach((field) => {
          record[field] = byKey[rowKey(range.value, field, condition)] || null;
        });
        return record;
      });
  }, [byKey, pinnedRanges, removedRanges]);

  const matrixData = useMemo(() => buildMatrix(''), [buildMatrix]);
  const highAbvData = useMemo(() => buildMatrix('high_abv_or_wild'), [buildMatrix]);

  const availableOverrides = useCallback((condition, visibleData) => {
    const visible = new Set(visibleData.map((r) => r.range));
    return OVERRIDE_RANGES.filter((r) => !visible.has(r.value));
  }, []);

  const clearDraftForCondition = useCallback((condition) => {
    setMarginDraft((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (next[k].condition === condition) delete next[k];
      });
      return next;
    });
  }, []);

  const touchMarginDraft = useCallback((
    range, field, condition, existing, patch,
  ) => {
    const k = rowKey(range, field, condition);
    setMarginDraft((prev) => {
      const prevEntry = prev[k];
      const basePct = existingTargetPct(existing);
      const base = prevEntry || {
        pct: basePct,
        rounding: existing?.rounding || DEFAULT_ROUNDING[field] || 'nearest_50',
        id: existing?.id,
        range,
        priceField: field,
        condition,
      };
      return {
        ...prev,
        [k]: {
          ...base,
          ...patch,
          range,
          priceField: field,
          condition,
          id: existing?.id ?? base.id,
        },
      };
    });
  }, []);

  const draftDirtyFor = useCallback((condition) => (
    Object.values(marginDraft).some((d) => d.condition === condition)
  ), [marginDraft]);

  const cancelMarginEdit = useCallback((condition) => {
    clearDraftForCondition(condition);
    setRemovedRanges((prev) => ({ ...prev, [condition || '']: [] }));
    setMarginEdit((prev) => ({ ...prev, [condition]: false }));
  }, [clearDraftForCondition]);

  const saveMarginEdit = useCallback(async (condition) => {
    const entries = Object.values(marginDraft)
      .filter((d) => d.condition === condition);
    if (!entries.length) {
      setMarginEdit((prev) => ({ ...prev, [condition]: false }));
      return;
    }
    setSavingMargins(true);
    try {
      await Promise.all(entries.map(async (d) => {
        if (d.pct == null) {
          if (d.id) await api.deleteMarginTarget(d.id);
          return;
        }
        await api.upsertMarginTarget({
          range: d.range,
          priceField: d.priceField,
          targetGpPct: d.pct,
          condition: d.condition || '',
          rounding: d.rounding || 'nearest_50',
        });
      }));
      clearDraftForCondition(condition);
      setRemovedRanges((prev) => ({ ...prev, [condition || '']: [] }));
      setMarginEdit((prev) => ({ ...prev, [condition]: false }));
      message.success('Targets saved');
      await loadAll({ silent: true });
    } catch (err) {
      message.error(err?.message || 'Failed to save targets');
    } finally {
      setSavingMargins(false);
    }
  }, [marginDraft, clearDraftForCondition, loadAll]);

  const makeColumns = useCallback((fields, condition) => {
    const editing = marginEdit[condition] === true;
    const cols = [
      {
        title: 'Range',
        dataIndex: 'rangeLabel',
        key: 'range',
        width: 100,
        render: (label, record) => {
          if (record.range === MARGIN_TARGET_DEFAULT_RANGE) {
            return (
              <Tooltip title="Used when a range has no override">
                <span>{label}</span>
              </Tooltip>
            );
          }
          if (editing) {
            return (
              <span className="pricing-guide-range-cell">
                {label}
                <Button
                  type="link"
                  size="small"
                  className="pricing-guide-unpin"
                  disabled={savingMargins}
                  onClick={() => removeRangeOverride(
                    condition, record.range, record,
                  )}
                >
                  Remove
                </Button>
              </span>
            );
          }
          return label;
        },
      },
    ];
    fields.forEach((field) => {
      const implied = IMPLIED_KEG_TARGET_PCT[field];
      cols.push({
        title: (
          <Tooltip title={implied != null ? `Suggested starting value ${implied}%` : 'Gross profit %'}>
            <span>{MARGIN_TARGET_FIELD_LABELS[field] || field}</span>
          </Tooltip>
        ),
        dataIndex: field,
        key: field,
        // Same width on each Strong/Wild pour col (tableLayout=fixed).
        ...(condition === 'high_abv_or_wild' ? { width: 150 } : {}),
        render: (existing, record) => {
          const k = rowKey(record.range, field, condition);
          const draft = marginDraft[k];
          const pct = draft && 'pct' in draft
            ? draft.pct
            : existingTargetPct(existing);
          const rounding = draft?.rounding
            || existing?.rounding
            || DEFAULT_ROUNDING[field]
            || 'nearest_50';
          return (
            <div className="pricing-guide-cell-stack">
              <TargetCell
                editing={editing}
                value={pct}
                impliedHint={implied}
                disabled={savingMargins}
                onChange={(v) => touchMarginDraft(
                  record.range, field, condition, existing, { pct: v },
                )}
              />
              {editing ? (
                <RoundingSelect
                  value={rounding}
                  onChange={(nextRounding) => {
                    const nextPct = pct != null ? pct : implied;
                    if (nextPct == null || Number.isNaN(nextPct)) return;
                    touchMarginDraft(
                      record.range, field, condition, existing, {
                        pct: nextPct,
                        rounding: nextRounding,
                      },
                    );
                  }}
                />
              ) : (
                <LockedRounding value={rounding} />
              )}
            </div>
          );
        },
      });
    });
    return cols;
  }, [marginEdit, marginDraft, savingMargins, touchMarginDraft, removeRangeOverride]);

  const matrixColumns = useMemo(
    () => makeColumns(MARGIN_TARGET_PRICE_FIELDS, ''),
    [makeColumns],
  );
  const highAbvColumns = useMemo(
    () => makeColumns(HIGH_ABV_FIELDS, 'high_abv_or_wild'),
    [makeColumns],
  );

  const progressiveLabel = progressiveChannelLabel(rules.progressive_unit_markup);

  const formulaData = POUR_FORMULA_ROWS.map((row) => {
    const fallbackD = KEG_FALLBACK_DIVISORS[row.key];
    const fallbackPct = IMPLIED_KEG_TARGET_PCT[row.snake];
    const activePct = resolveTargetPct(targetMap, '*', row.key, '');
    const highPct = resolveTargetPct(targetMap, '*', row.key, 'high_abv_or_wild');
    const activeD = activePct != null
      ? targetPctToDivisor(activePct)
      : fallbackD;
    const highD = highPct != null ? targetPctToDivisor(highPct) : null;
    const displayActivePct = activePct != null ? activePct : fallbackPct;
    const displayActiveD = activePct != null ? activeD : fallbackD;
    return {
      ...row,
      fallbackNode: formatPctWithDivisor(fallbackPct, fallbackD),
      activeNode: formatPctWithDivisor(displayActivePct, displayActiveD),
      highNode: highPct != null ? formatPctWithDivisor(highPct, highD) : '—',
    };
  });

  return (
    <div className="pricing-guide-page">
      <PricingExample targetMap={targetMap} rules={rules} />

      <section className="pricing-guide-margins">
        <Title level={4} className="pricing-guide-margins-title">
          Margins and Guidelines
        </Title>

        <RulesCard rules={rules} onSave={saveRules} />

        <Card
          size="small"
          title={(
            <Tooltip title="Gross profit % targets by range and price field">
              <span>Target margins</span>
            </Tooltip>
          )}
          extra={(
            <MarginsCardActions
              editing={marginEdit[''] === true}
              saving={savingMargins}
              dirty={draftDirtyFor('')}
              onEdit={() => setMarginEdit((prev) => ({ ...prev, '': true }))}
              onCancel={() => cancelMarginEdit('')}
              onSave={() => saveMarginEdit('')}
              addOverride={(
                <AddRangeOverride
                  available={availableOverrides('', matrixData)}
                  onAdd={(rangeValue) => pinRange('', rangeValue)}
                />
              )}
            />
          )}
          className="pricing-guide-section"
        >
          <Table
            className="pricing-guide-matrix"
            size="middle"
            loading={loading}
            pagination={false}
            tableLayout="fixed"
            rowKey="key"
            dataSource={matrixData}
            columns={matrixColumns}
          />
        </Card>

        <Card
          size="small"
          title={(
            <Tooltip
              title={`Strong threshold or Type=Wild. Wild under ${PINT_MAX_ABV}% ABV uses this Pint target; over ${PINT_MAX_ABV}% skips pint.`}
            >
              <span>Strong beer / Wild</span>
            </Tooltip>
          )}
          extra={(
            <MarginsCardActions
              editing={marginEdit.high_abv_or_wild === true}
              saving={savingMargins}
              dirty={draftDirtyFor('high_abv_or_wild')}
              onEdit={() => setMarginEdit((prev) => ({
                ...prev,
                high_abv_or_wild: true,
              }))}
              onCancel={() => cancelMarginEdit('high_abv_or_wild')}
              onSave={() => saveMarginEdit('high_abv_or_wild')}
              addOverride={(
                <AddRangeOverride
                  available={availableOverrides('high_abv_or_wild', highAbvData)}
                  onAdd={(rangeValue) => pinRange('high_abv_or_wild', rangeValue)}
                />
              )}
            />
          )}
          className="pricing-guide-section"
        >
          <Table
            className="pricing-guide-matrix pricing-guide-matrix-strong"
            size="middle"
            loading={loading}
            pagination={false}
            tableLayout="fixed"
            rowKey="key"
            dataSource={highAbvData}
            columns={highAbvColumns}
          />
        </Card>

        <Collapse
          ghost
          className="pricing-guide-collapse"
          defaultActiveKey={['formulas', 'channels']}
        >
          <Collapse.Panel header="How pour prices work" key="formulas">
            <Table
              size="middle"
              pagination={false}
              rowKey="key"
              dataSource={formulaData}
              columns={[
                {
                  title: 'Pour',
                  dataIndex: 'label',
                  key: 'label',
                  width: 160,
                },
                {
                  title: 'How it is calculated',
                  dataIndex: 'formula',
                  key: 'formula',
                  render: (t) => <span className="pricing-guide-formula">{t}</span>,
                },
                {
                  title: 'Fallback',
                  dataIndex: 'fallbackNode',
                  key: 'fallback',
                  width: 100,
                },
                {
                  title: 'Default',
                  dataIndex: 'activeNode',
                  key: 'active',
                  width: 100,
                },
                {
                  title: 'Strong / Wild',
                  dataIndex: 'highNode',
                  key: 'high',
                  width: 110,
                },
              ]}
            />
          </Collapse.Panel>
          <Collapse.Panel header="Channel rules" key="channels">
            <Row gutter={[16, 16]} className="pricing-guide-cards">
              <Col xs={24} md={12}>
                <Card size="small" title="GST">
                  NZ GST multiplier
                  {' '}
                  <Text strong>{GST_MULT}</Text>
                  . Trade prices are ex GST. RRP, pours, and can-pour are inc GST.
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Progressive">
                  Off Prem ×
                  {' '}
                  <Text strong>
                    {progressiveLabel.mult}
                  </Text>
                  {' '}
                  (+
                  {progressiveLabel.pct}
                  %). 4×6 cases and SKUs listed under Rules stay plain Off Prem.
                  Not used for kegs.
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Cin7 Corporate (Tier 5)">
                  On Prem ×
                  {' '}
                  <Text strong>1.1</Text>
                  , rounded. Tiers 1/9 = On Prem; 2/4/8 = Off Prem; 10 = Progressive.
                  Export / AU / HK tiers are not managed here.
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Kegs">
                  Kegs use On Prem for both on- and off-premise. Off, Progressive,
                  and can-pour are greyed. Pack/case RRP do not apply.
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Cost basis">
                  RRP cost = Off Prem ÷ case units. Pour cost = On Prem ÷ keg litres
                  × pour size (
                  {KEG_POUR_VOLUME_L.pricePint}
                  L /
                  {' '}
                  {KEG_POUR_VOLUME_L.priceHalfPint}
                  L /
                  {' '}
                  {KEG_POUR_VOLUME_L.price150ml}
                  L).
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Can-pour + Progressive retail">
                  Can-pour = Off Prem unit cost ÷ (1 − GP%) × 1.15 (330 / 440 / Wild
                  margins in Rules). Progressive retail unit = RRP Unit + markup %,
                  nearest 50¢. Progressive retail pack uses pack margin, nearest cent.
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="RRP Case / Shopify slab">
                  Case/slab RRP = RRP Unit × case units × (1 − discount).
                  {' '}
                  {formatNiceNumber(storageToUi(
                    { ui: 'count', key: 'slab_case_large_units' },
                    rules.slab_case_large_units,
                  ))}
                  -unit slabs −
                  {formatNiceNumber(storageToUi(
                    { ui: 'discount_pct', key: 'slab_case_discount_large' },
                    rules.slab_case_discount_large,
                  ))}
                  %; smaller −
                  {formatNiceNumber(storageToUi(
                    { ui: 'discount_pct', key: 'slab_case_discount_small' },
                    rules.slab_case_discount_small,
                  ))}
                  %. Same rule drives Shopify Product Builder slabs. Playground
                  rounds to 50¢; Shopify keeps 2dp.
                </Card>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
      </section>

      <MondayFormulaCheck />
    </div>
  );
};

export default PricingGuide;
